export interface CockpitNavItem {
  readonly path: string
  readonly label: string
  readonly end: boolean
  readonly badge?: string
}

export const cockpitMainNavigation: readonly CockpitNavItem[] = [
  { path: '/', label: 'Übersicht', end: true },
  { path: '/margin', label: 'Marge', end: true },
  { path: '/trends', label: 'Trends', end: true },
  { path: '/weather', label: 'Wetter & Saison', end: true },
  { path: '/producttypes', label: 'Produkttypen & Prognosen', end: true },
  { path: '/content', label: 'Content / Instagram', end: true, badge: 'Später' },
]

export type CockpitPageId = 'margin' | 'trends' | 'weather' | 'producttypes' | 'content'

export interface CockpitKpi {
  readonly label: string
  readonly value: string
  readonly context: string
}

export interface CockpitSignal {
  readonly title: string
  readonly value: string
  readonly detail: string
  readonly strength: number
  readonly tone: 'positive' | 'watch' | 'neutral'
}

export interface CockpitSubview {
  readonly id: string
  readonly label: string
  readonly description: string
}

export interface CockpitPageDefinition {
  readonly id: CockpitPageId
  readonly path: string
  readonly navLabel: string
  readonly title: string
  readonly eyebrow: string
  readonly description: string
  readonly demoSource: string
  readonly updatedAt: string
  readonly kpis: readonly CockpitKpi[]
  readonly signals: readonly CockpitSignal[]
  readonly subviews: readonly CockpitSubview[]
  readonly calculationNote: string
  readonly dependencies: readonly string[]
}

export const overviewKpis: readonly CockpitKpi[] = [
  {
    label: 'Aktive Signale',
    value: '18',
    context: 'Demo aus fünf Cockpit-Bereichen',
  },
  {
    label: 'Beobachtete Produkttypen',
    value: '42',
    context: 'Dummy-Katalog für die erste Struktur',
  },
  {
    label: 'Datenquellen',
    value: '4',
    context: 'Noch nicht mit Live-Verträgen verbunden',
  },
  {
    label: 'Letzte Aktualisierung',
    value: '09:30',
    context: 'Demo-Stand vom 04.08.2026',
  },
]

export const analysisFlow: readonly CockpitSubview[] = [
  {
    id: 'signal',
    label: 'Signal erkennen',
    description: 'Auffällige Marge, Trend-, Wetter- oder Content-Signale zuerst sichtbar machen.',
  },
  {
    id: 'context',
    label: 'Zeitraum und Quelle prüfen',
    description: 'Zeitraum, Datenquelle und Aktualität eines Signals nachvollziehbar einordnen.',
  },
  {
    id: 'producttype',
    label: 'Produkttypen einordnen',
    description: 'Betroffene Produkttypen, Kollektionen, Motive und Themen zusammenführen.',
  },
  {
    id: 'action',
    label: 'Handlung ableiten',
    description: 'Aus der Einordnung eine konkrete Priorität für Produkt oder Marketing ableiten.',
  },
]

const commonSubViews: readonly CockpitSubview[] = [
  {
    id: 'overview',
    label: 'Überblick',
    description: 'Kernsignale und KPI-Karten für die schnelle Einordnung.',
  },
  {
    id: 'comparison',
    label: 'Vergleich',
    description: 'Gruppen, Produkttypen und Zeiträume direkt vergleichen.',
  },
  {
    id: 'actions',
    label: 'Handlungsimpulse',
    description: 'Beobachtungen in priorisierte nächste Schritte übersetzen.',
  },
]

export const cockpitPageDefinitions: Readonly<Record<CockpitPageId, CockpitPageDefinition>> = {
  margin: {
    id: 'margin',
    path: '/margin',
    navLabel: 'Marge',
    title: 'Margenübersicht',
    eyebrow: 'Marge',
    description:
      'Kollektionen, Motive, Themengruppen und Themen nach ihrer Wirtschaftlichkeit vergleichbar machen.',
    demoSource: 'Demo-Daten: Produkt- und Kostenmodell',
    updatedAt: '04.08.2026, 09:30 Uhr',
    kpis: [
      { label: 'Ø Deckungsbeitrag', value: '42 %', context: 'Dummy-Wert über alle Demo-Produkte' },
      { label: 'Top-Margen-Gruppe', value: 'Deko', context: 'Dummy-Rang nach Deckungsbeitrag' },
      { label: 'Beobachtung', value: '6', context: 'Demo-Produkte unter Zielmarge' },
      { label: 'Vergleichszeitraum', value: '30 Tage', context: 'Im Filter veränderbar' },
    ],
    signals: [
      {
        title: 'Deko',
        value: '48 %',
        detail: 'Stabile Demo-Marge im aktuellen Zeitraum',
        strength: 88,
        tone: 'positive',
      },
      {
        title: 'Textilien',
        value: '36 %',
        detail: 'Unter dem Demo-Ziel, aber mit positiver Tendenz',
        strength: 67,
        tone: 'watch',
      },
      {
        title: 'Karten',
        value: '28 %',
        detail: 'Demo-Signal für Kosten- oder Preisprüfung',
        strength: 48,
        tone: 'watch',
      },
    ],
    subviews: commonSubViews,
    calculationNote:
      'Demo-Logik: Deckungsbeitrag geteilt durch Verkaufspreis. Kostenbestandteile, Retouren und Zeitraumregeln sind noch nicht mit einem API-Vertrag verbunden.',
    dependencies: [
      'Produkt- und Kostenfelder je Auswertungsebene',
      'Verbindliche Definition von Umsatz, Kosten, Retouren und Deckungsbeitrag',
      'Serverseitige Filterung, Sortierung und Pagination für große Ergebnismengen',
    ],
  },
  trends: {
    id: 'trends',
    path: '/trends',
    navLabel: 'Trends',
    title: 'Trendübersicht',
    eyebrow: 'Trends',
    description:
      'Aktuelle und kommende Themen als Signale bündeln und nach Quelle sowie Produkttyp einordnen.',
    demoSource: 'Demo-Daten: Pinterest und TikTok',
    updatedAt: '04.08.2026, 08:45 Uhr',
    kpis: [
      { label: 'Trend-Signale', value: '12', context: 'Dummy-Signale aus zwei Quellen' },
      { label: 'Steigend', value: '7', context: 'Demo-Signale mit positiver Dynamik' },
      { label: 'Quellen', value: '2', context: 'Pinterest und TikTok als Platzhalter' },
      { label: 'Signalalter', value: '< 24 h', context: 'Zielbild für die spätere Aktualisierung' },
    ],
    signals: [
      {
        title: 'Botanische Muster',
        value: '+38 %',
        detail: 'Demo-Potenzial für Deko und Textilien',
        strength: 84,
        tone: 'positive',
      },
      {
        title: 'Minimalismus',
        value: '+21 %',
        detail: 'Demo-Signal mit breiter Produkttyp-Abdeckung',
        strength: 62,
        tone: 'neutral',
      },
      {
        title: 'Personalisierung',
        value: 'prüfen',
        detail: 'Quelle und Messmethode müssen noch festgelegt werden',
        strength: 42,
        tone: 'watch',
      },
    ],
    subviews: [
      { id: 'overview', label: 'Überblick', description: 'Signale nach Dynamik und Quelle.' },
      {
        id: 'sources',
        label: 'Quellenvergleich',
        description: 'Pinterest und TikTok getrennt prüfen.',
      },
      {
        id: 'forecast',
        label: 'Kommende Trends',
        description: 'Frühe Signale für die Planung markieren.',
      },
    ],
    calculationNote:
      'Demo-Logik: Trendstärke als Kombination aus Wachstum, Reichweite und Aktualität. Ein echter Vertrag muss Quellen, Zeitfenster, Entdoppelung und Vergleichsbasis definieren.',
    dependencies: [
      'Zugriff und Nutzungsrechte für Pinterest- und TikTok-Daten',
      'Einheitliche Definition von Trend, Wachstum, Reichweite und Aktualität',
      'Zeitstempel, Quellennachweis und stabile externe IDs je Signal',
    ],
  },
  weather: {
    id: 'weather',
    path: '/weather',
    navLabel: 'Wetter & Saison',
    title: 'Wetterentwicklung und Saison',
    eyebrow: 'Wetter & Saison',
    description:
      'Die Wetterentwicklung der nächsten Tage als Kontext für Sortimente und saisonale Planung sichtbar machen.',
    demoSource: 'Demo-Daten: Wetterdienst',
    updatedAt: '04.08.2026, 09:00 Uhr',
    kpis: [
      { label: 'Temperatur', value: '18 °C', context: 'Dummy-Mittelwert für die nächsten Tage' },
      { label: 'Niederschlag', value: '35 %', context: 'Demo-Wahrscheinlichkeit im Zeitraum' },
      { label: 'Prognosefenster', value: '7 Tage', context: 'Im Filter veränderbar' },
      { label: 'Saisonphase', value: 'Spätsommer', context: 'Dummy-Klassifikation' },
    ],
    signals: [
      {
        title: 'Warm',
        value: '3 Tage',
        detail: 'Demo-Hinweis auf leichte, sommerliche Produkttypen',
        strength: 76,
        tone: 'positive',
      },
      {
        title: 'Wechselhaft',
        value: '2 Tage',
        detail: 'Demo-Hinweis auf flexible Übergangsprodukte',
        strength: 57,
        tone: 'watch',
      },
      {
        title: 'Kaltfront',
        value: 'in 6 Tagen',
        detail: 'Frühes Demo-Signal für wärmere Produkttypen',
        strength: 39,
        tone: 'neutral',
      },
    ],
    subviews: [
      {
        id: 'overview',
        label: '7-Tage-Verlauf',
        description: 'Temperatur und Niederschlag im Zeitverlauf.',
      },
      {
        id: 'season',
        label: 'Saisonkontext',
        description: 'Saisonale Einordnung für Planung und Sortiment.',
      },
      {
        id: 'signals',
        label: 'Wetterimpulse',
        description: 'Wetterlagen mit möglichen Produktbezügen.',
      },
    ],
    calculationNote:
      'Demo-Logik: Wetterlage aus Temperatur, Niederschlag und Veränderung gegenüber dem Vortag. Prognosen sind keine Absatzprognosen und müssen regional sowie zeitlich abgegrenzt werden.',
    dependencies: [
      'Wetterquelle mit Region, Vorhersagezeitpunkt und Aktualitätsangabe',
      'Festgelegte Regionen und Zeitzonen für die Auswertung',
      'Regeln zur Zuordnung von Wetterlagen und Saisonphasen',
    ],
  },
  producttypes: {
    id: 'producttypes',
    path: '/producttypes',
    navLabel: 'Produkttypen & Prognosen',
    title: 'Produkttypen und Prognosen',
    eyebrow: 'Produkttypen & Prognosen',
    description:
      'Erkennen, welche Produkttypen zu kaltem, warmem und wechselndem Wetter sowie zu Jahreszeiten passen.',
    demoSource: 'Demo-Daten: Wetter plus Produkttypen',
    updatedAt: '04.08.2026, 09:15 Uhr',
    kpis: [
      { label: 'Kälte geeignet', value: '24', context: 'Dummy-Produkttypen mit hohem Fit' },
      { label: 'Wärme geeignet', value: '18', context: 'Dummy-Produkttypen mit hohem Fit' },
      { label: 'Wechselhaft geeignet', value: '11', context: 'Demo-Schnittmenge' },
      { label: 'Prognosevertrauen', value: '72 %', context: 'Nur ein Strukturplatzhalter' },
    ],
    signals: [
      {
        title: 'Hoodies',
        value: 'kalt',
        detail: 'Demo-Fit für sinkende Temperaturen',
        strength: 86,
        tone: 'positive',
      },
      {
        title: 'Tassen',
        value: 'wechselhaft',
        detail: 'Demo-Fit mit saisonal breiter Verwendbarkeit',
        strength: 65,
        tone: 'neutral',
      },
      {
        title: 'Sommermotive',
        value: 'warm',
        detail: 'Demo-Fit für warme Tage und Spätsommer',
        strength: 73,
        tone: 'positive',
      },
    ],
    subviews: [
      {
        id: 'overview',
        label: 'Produkttyp-Fit',
        description: 'Zuordnung von Wetterlage und Produkttyp.',
      },
      {
        id: 'season',
        label: 'Jahreszeiten',
        description: 'Saisonale Muster und Übergänge vergleichen.',
      },
      {
        id: 'forecast',
        label: 'Prognose',
        description: 'Erwartete Relevanz mit Vertrauenshinweis.',
      },
    ],
    calculationNote:
      'Demo-Logik: Produkttyp-Fit aus Wetterlage, Saison und historischen Signalen. Ein echter Forecast braucht Trainingsfenster, Zielgröße, Gütemaße und eine Regel für Unsicherheit.',
    dependencies: [
      'Verknüpfung von Produkttypen, Kollektionen, Motiven, Themengruppen und Themen',
      'Historische Absatz- oder Interaktionsdaten mit Wetter- und Saisonbezug',
      'Dokumentierte Prognosegüte, Aktualisierung und Umgang mit neuen Produkttypen',
    ],
  },
  content: {
    id: 'content',
    path: '/content',
    navLabel: 'Content / Instagram',
    title: 'Content und Instagram',
    eyebrow: 'Späterer Ausbau',
    description:
      'Best-Performer-Posts und Content-Signale für Instagram werden in einer späteren Ausbaustufe ergänzt.',
    demoSource: 'Noch keine Schnittstelle verbunden',
    updatedAt: 'Noch nicht verfügbar',
    kpis: [
      { label: 'Best-Performer-Posts', value: 'später', context: 'Noch keine Live-Daten' },
      { label: 'Engagement-Rate', value: 'später', context: 'Berechnung noch offen' },
      { label: 'Content-Signale', value: 'später', context: 'Quelle und Zeitraum fehlen' },
      { label: 'Status', value: 'geplant', context: 'Bereich ist als Platzhalter sichtbar' },
    ],
    signals: [
      {
        title: 'Post-Ranking',
        value: 'später',
        detail: 'Schnittstelle für Posts, Reichweite und Interaktionen fehlt',
        strength: 12,
        tone: 'watch',
      },
      {
        title: 'Formatvergleich',
        value: 'später',
        detail: 'Reels, Bilder und Stories sind noch nicht vergleichbar',
        strength: 8,
        tone: 'neutral',
      },
      {
        title: 'Produkttyp-Bezug',
        value: 'später',
        detail: 'Zuordnung von Content zu Produkten und Themen fehlt',
        strength: 5,
        tone: 'neutral',
      },
    ],
    subviews: [
      {
        id: 'overview',
        label: 'Best Performer',
        description: 'Geplante Rangliste der stärksten Posts.',
      },
      { id: 'formats', label: 'Formate', description: 'Geplanter Vergleich von Content-Formaten.' },
      {
        id: 'products',
        label: 'Produktbezug',
        description: 'Geplante Verbindung zu Produkttypen und Themen.',
      },
    ],
    calculationNote:
      'Noch keine Berechnung aktiv. Für eine belastbare Auswertung müssen Datenquelle, Einwilligung, Zeitraum, Reichweitenbasis und Zuordnung zu Produkten verbindlich definiert werden.',
    dependencies: [
      'Instagram-Datenzugriff mit zulässigen Feldern und Aktualisierungsregeln',
      'Definition von Best Performer, Engagement-Rate und Vergleichszeitraum',
      'Stabile Zuordnung von Posts zu Produkttypen, Motiven und Themen',
    ],
  },
}
